import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Receipt, 
  Plus, 
  Eye, 
  Send, 
  Download,
  Pencil,
  Trash2,
  FileText,
  TrendingUp,
  X,
  Check,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Users,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DatePicker } from '../components/DatePicker';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatDate, getMonthName } from '../lib/utils';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  'Brouillon': 'bg-slate-100 text-slate-700',
  'Envoyée': 'bg-blue-100 text-blue-700',
  'Payée': 'bg-emerald-100 text-emerald-700'
};

const CHART_COLORS = ['#175CD3', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'];

const RECURRENCE_FILTERS = ['Toutes', 'Mensuelle', 'Trimestrielle', 'Semestrielle', 'Annuelle', 'Ponctuelle'];

// ============== STEPPER COMPONENT ==============
const Stepper = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div className="flex items-center">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
            ${index < currentStep ? 'bg-emerald-500 text-white' : 
              index === currentStep ? 'bg-primary text-white' : 
              'bg-slate-100 text-slate-400'}
          `}>
            {index < currentStep ? <Check size={18} /> : index + 1}
          </div>
          <span className={`ml-2 text-sm font-medium ${
            index <= currentStep ? 'text-slate-900' : 'text-slate-400'
          }`}>
            {step}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div className={`w-16 h-0.5 mx-4 ${
            index < currentStep ? 'bg-emerald-500' : 'bg-slate-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============== BATCH INVOICING COMPONENT ==============
const FacturationEnLot = ({ clients, onComplete }) => {
  const [step, setStep] = useState(0);
  const [period, setPeriod] = useState('');
  const [recurrenceFilter, setRecurrenceFilter] = useState('Toutes');
  const [selectedClients, setSelectedClients] = useState({});
  const [clientData, setClientData] = useState({});
  const [expandedClients, setExpandedClients] = useState({});
  const [createdInvoices, setCreatedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter clients with prestations matching recurrence
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      if (!client.prestations || client.prestations.length === 0) return false;
      if (recurrenceFilter === 'Toutes') return true;
      return client.prestations.some(p => p.recurrence === recurrenceFilter);
    });
  }, [clients, recurrenceFilter]);

  // Calculate totals for a client
  const calculateClientTotals = (clientId) => {
    const data = clientData[clientId];
    if (!data) return { totalHT: 0, vat: 0, totalTTC: 0 };
    
    const selectedLines = data.lines.filter(l => l.selected);
    const totalHT = selectedLines.reduce((sum, l) => sum + l.amount, 0);
    const vat = totalHT * (data.vatRate / 100);
    const totalTTC = totalHT + vat;
    
    return { totalHT, vat, totalTTC };
  };

  // Initialize client data when moving to step 2
  const initializeClientData = () => {
    const newClientData = {};
    Object.keys(selectedClients).forEach(clientId => {
      if (!selectedClients[clientId]) return;
      
      const client = clients.find(c => c.id === clientId);
      if (!client) return;
      
      const filteredPrestations = recurrenceFilter === 'Toutes' 
        ? client.prestations 
        : client.prestations.filter(p => p.recurrence === recurrenceFilter);
      
      // Determine default VAT rate based on country
      const defaultVat = client.country === 'Suisse' ? 8.1 : 0;
      
      newClientData[clientId] = {
        vatRate: defaultVat,
        object: 'Conseils et assistance',
        lines: filteredPrestations.map(p => ({
          prestation_id: p.id,
          description: `${p.name}${period ? ` - ${period}` : ''}`,
          amount: p.amount_ht,
          referenceAmount: p.amount_ht,
          selected: true
        }))
      };
    });
    setClientData(newClientData);
    
    // Expand first client by default
    const firstClient = Object.keys(newClientData)[0];
    if (firstClient) {
      setExpandedClients({ [firstClient]: true });
    }
  };

  const handleSelectAll = (selected) => {
    const newSelected = {};
    filteredClients.forEach(c => {
      newSelected[c.id] = selected;
    });
    setSelectedClients(newSelected);
  };

  const getSelectedCount = () => {
    return Object.values(selectedClients).filter(Boolean).length;
  };

  const getGlobalTotals = () => {
    let totalHT = 0;
    let totalVAT = 0;
    let totalTTC = 0;
    
    Object.keys(clientData).forEach(clientId => {
      const totals = calculateClientTotals(clientId);
      totalHT += totals.totalHT;
      totalVAT += totals.vat;
      totalTTC += totals.totalTTC;
    });
    
    return { totalHT, totalVAT, totalTTC };
  };

  const handleGenerateInvoices = async () => {
    setLoading(true);
    try {
      const clientsToInvoice = Object.keys(clientData).map(clientId => ({
        client_id: clientId,
        object: clientData[clientId].object || 'Conseils et assistance',
        lines: clientData[clientId].lines.map(l => ({
          prestation_id: l.prestation_id,
          description: l.description,
          amount: l.amount,
          selected: l.selected
        })),
        vat_rate: clientData[clientId].vatRate
      }));
      
      const response = await axios.post(`${API_URL}/invoices/batch`, {
        period,
        clients: clientsToInvoice
      });
      
      setCreatedInvoices(response.data.invoices);
      toast.success(response.data.message);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAll = async () => {
    setLoading(true);
    try {
      const invoiceIds = createdInvoices.map(inv => inv.id);
      const response = await axios.post(`${API_URL}/invoices/send-batch`, invoiceIds);
      toast.success(response.data.message);
      
      // Update local state
      setCreatedInvoices(prev => prev.map(inv => ({ ...inv, status: 'Envoyée' })));
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendSingle = async (invoice) => {
    try {
      const response = await axios.post(`${API_URL}/invoices/${invoice.id}/send`);
      toast.success(response.data.message || 'Email envoyé');
      setCreatedInvoices(prev => prev.map(inv => 
        inv.id === invoice.id ? { ...inv, status: 'Envoyée' } : inv
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  const steps = ['Sélection', 'Validation', 'Confirmation'];

  return (
    <div className="space-y-6">
      <Stepper currentStep={step} steps={steps} />

      {/* Step 1: Selection */}
      {step === 0 && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Période de facturation</Label>
                <Input
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  placeholder="Ex: Mars 2026, Q1 2026, 2025"
                  data-testid="batch-period"
                />
              </div>
              <div className="space-y-2">
                <Label>Filtrer par récurrence</Label>
                <Select value={recurrenceFilter} onValueChange={setRecurrenceFilter}>
                  <SelectTrigger data-testid="batch-recurrence-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_FILTERS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Clients ({filteredClients.length})</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleSelectAll(true)} className="rounded-lg">
                  Tout sélectionner
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleSelectAll(false)} className="rounded-lg">
                  Tout désélectionner
                </Button>
              </div>
            </div>
            
            {filteredClients.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Users size={48} className="mx-auto mb-4 text-slate-300" />
                <p>Aucun client avec des prestations correspondant au filtre</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredClients.map(client => {
                  const filteredPrestations = recurrenceFilter === 'Toutes'
                    ? client.prestations
                    : client.prestations.filter(p => p.recurrence === recurrenceFilter);
                  const totalEstimated = filteredPrestations.reduce((sum, p) => sum + p.amount_ht, 0);
                  
                  return (
                    <div key={client.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`batch-client-${client.id}`}>
                      <Checkbox
                        checked={selectedClients[client.id] || false}
                        onCheckedChange={(checked) => setSelectedClients(prev => ({ ...prev, [client.id]: checked }))}
                        data-testid={`batch-select-${client.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{client.company_name}</p>
                        <p className="text-sm text-slate-500">{filteredPrestations.length} prestation(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums">{formatCurrency(totalEstimated)}</p>
                        <p className="text-xs text-slate-500">estimé HT</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={() => { initializeClientData(); setStep(1); }}
              disabled={getSelectedCount() === 0 || !period}
              className="rounded-lg"
              data-testid="batch-next-step1"
            >
              Suivant ({getSelectedCount()} client(s))
              <ChevronRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Validation */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="space-y-4">
            {Object.keys(clientData).map(clientId => {
              const client = clients.find(c => c.id === clientId);
              const data = clientData[clientId];
              const totals = calculateClientTotals(clientId);
              const isExpanded = expandedClients[clientId];
              
              return (
                <Collapsible key={clientId} open={isExpanded} onOpenChange={(open) => setExpandedClients(prev => ({ ...prev, [clientId]: open }))}>
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`expand-client-${clientId}`}>
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        <span className="font-semibold">{client?.company_name}</span>
                        <Badge variant="outline">{data.lines.filter(l => l.selected).length} ligne(s)</Badge>
                      </div>
                      <span className="font-bold tabular-nums">{formatCurrency(totals.totalTTC)} TTC</span>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="border-t p-4 space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`batch-object-${clientId}`}>Objet de la facture</Label>
                          <Input
                            id={`batch-object-${clientId}`}
                            placeholder="Ex: Conseils et assistance"
                            value={data.object || ''}
                            onChange={(e) => {
                              const newObj = e.target.value;
                              setClientData(prev => ({
                                ...prev,
                                [clientId]: { ...prev[clientId], object: newObj }
                              }));
                            }}
                            data-testid={`batch-object-${clientId}`}
                          />
                        </div>
                        {data.lines.map((line, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                            <Checkbox
                              checked={line.selected}
                              onCheckedChange={(checked) => {
                                setClientData(prev => ({
                                  ...prev,
                                  [clientId]: {
                                    ...prev[clientId],
                                    lines: prev[clientId].lines.map((l, i) => 
                                      i === idx ? { ...l, selected: checked } : l
                                    )
                                  }
                                }));
                              }}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{line.description}</p>
                              <p className="text-xs text-slate-500">Référence: {formatCurrency(line.referenceAmount)}</p>
                            </div>
                            <Input
                              type="number"
                              value={line.amount}
                              onChange={(e) => {
                                const newAmount = parseFloat(e.target.value) || 0;
                                setClientData(prev => ({
                                  ...prev,
                                  [clientId]: {
                                    ...prev[clientId],
                                    lines: prev[clientId].lines.map((l, i) => 
                                      i === idx ? { ...l, amount: newAmount } : l
                                    )
                                  }
                                }));
                              }}
                              className="w-28 h-9 text-right"
                              disabled={!line.selected}
                            />
                            <span className="text-sm text-slate-500 w-12">CHF</span>
                          </div>
                        ))}
                        
                        <div className="flex items-center justify-between pt-3 border-t">
                          <div className="flex items-center gap-2">
                            <Label>TVA:</Label>
                            <Select 
                              value={data.vatRate.toString()} 
                              onValueChange={(v) => {
                                setClientData(prev => ({
                                  ...prev,
                                  [clientId]: { ...prev[clientId], vatRate: parseFloat(v) }
                                }));
                              }}
                            >
                              <SelectTrigger className="w-32 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="8.1">8.1% (Standard)</SelectItem>
                                <SelectItem value="0">0% (Reverse charge)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="text-right text-sm">
                            <p>Total HT: <span className="font-semibold">{formatCurrency(totals.totalHT)}</span></p>
                            <p>TVA: <span className="font-semibold">{formatCurrency(totals.vat)}</span></p>
                            <p className="text-base">Total TTC: <span className="font-bold">{formatCurrency(totals.totalTTC)}</span></p>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>

          {/* Global summary */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Récapitulatif global</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Nombre de factures</p>
                <p className="text-xl font-bold">{Object.keys(clientData).length}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total HT</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(getGlobalTotals().totalHT)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total TVA</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(getGlobalTotals().totalVAT)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total TTC</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(getGlobalTotals().totalTTC)}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)} className="rounded-lg">
              Retour
            </Button>
            <Button onClick={handleGenerateInvoices} disabled={loading} className="rounded-lg" data-testid="batch-generate">
              {loading ? 'Génération...' : 'Générer les factures'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <Check size={48} className="mx-auto mb-2 text-emerald-600" />
            <h3 className="text-lg font-semibold text-emerald-800">
              {createdInvoices.length} facture(s) créée(s) avec succès !
            </h3>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Factures générées</h3>
              <Button onClick={handleSendAll} disabled={loading} className="rounded-lg" data-testid="batch-send-all">
                <Send size={16} className="mr-2" />
                Tout envoyer par email
              </Button>
            </div>
            
            <table className="data-table">
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Client</th>
                  <th>Montant TTC</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {createdInvoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td className="font-mono">{invoice.invoice_number}</td>
                    <td>{getClientName(invoice.client_id)}</td>
                    <td className="tabular-nums">{formatCurrency(invoice.total_ttc)}</td>
                    <td>
                      <Badge className={statusColors[invoice.status]}>{invoice.status}</Badge>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(invoice)} className="rounded-lg">
                          <Download size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSendSingle(invoice)} className="rounded-lg">
                          <Send size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center">
            <Button variant="outline" onClick={() => { setStep(0); setCreatedInvoices([]); onComplete(); }} className="rounded-lg">
              Retour à la facturation
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== MANUAL INVOICE COMPONENT ==============
const FactureManuelle = ({ clients, onComplete }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    payment_terms: '30 jours net',
    period: '',
    object: 'Conseils et assistance',
    vat_rate: 8.1,
    lines: [],
    notes: ''
  });

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData(prev => ({ 
      ...prev, 
      client_id: clientId,
      vat_rate: client?.country === 'Suisse' ? 8.1 : 0
    }));
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { id: Date.now().toString(), description: '', quantity: 1, unit_price: 0, total: 0 }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        lines[index].total = lines[index].quantity * lines[index].unit_price;
      }
      return { ...prev, lines };
    });
  };

  const removeLine = (index) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const totalHT = formData.lines.reduce((sum, l) => sum + (l.total || l.quantity * l.unit_price), 0);
    const vatAmount = totalHT * (formData.vat_rate / 100);
    const totalTTC = totalHT + vatAmount;
    return { totalHT, vatAmount, totalTTC };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.lines.length === 0) {
      toast.error('Veuillez ajouter au moins une ligne');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/invoices`, {
        ...formData,
        status: 'Brouillon'
      });
      toast.success('Facture créée');
      onComplete();
      // Reset form
      setFormData({
        client_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        payment_terms: '30 jours net',
        period: '',
        vat_rate: 8.1,
        lines: [],
        notes: ''
      });
      setSelectedClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const { totalHT, vatAmount, totalTTC } = calculateTotals();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
        <h3 className="font-semibold">Client</h3>
        <Select value={formData.client_id} onValueChange={handleClientSelect}>
          <SelectTrigger data-testid="manual-client">
            <SelectValue placeholder="Sélectionner un client" />
          </SelectTrigger>
          <SelectContent>
            {clients.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="space-y-2">
          <Label htmlFor="manual-object">Objet de la facture</Label>
          <Input
            id="manual-object"
            placeholder="Ex: Conseils et assistance"
            value={formData.object}
            onChange={(e) => setFormData(prev => ({ ...prev, object: e.target.value }))}
            data-testid="manual-object"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Lignes de facture</h3>
          <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-lg" data-testid="manual-add-line">
            <Plus size={16} className="mr-1" />
            Ajouter une ligne
          </Button>
        </div>
        
        {formData.lines.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Aucune ligne</p>
        ) : (
          <div className="space-y-3">
            {formData.lines.map((line, index) => (
              <div key={line.id} className="flex gap-3 items-start bg-slate-50 p-3 rounded-lg">
                <div className="flex-1">
                  <Input
                    placeholder="Description"
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    className="mb-2"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Qté"
                      value={line.quantity}
                      onChange={(e) => updateLine(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      placeholder="Prix unit."
                      value={line.unit_price}
                      onChange={(e) => updateLine(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-28"
                    />
                    <span className="flex items-center text-sm text-slate-500">
                      = {formatCurrency(line.quantity * line.unit_price)}
                    </span>
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(index)} className="text-red-500">
                  <X size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
        <h3 className="font-semibold">Détails</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date de facturation</Label>
            <DatePicker
              value={formData.invoice_date}
              onChange={(iso) => setFormData(prev => ({ ...prev, invoice_date: iso }))}
              testId="manual-invoice-date"
            />
          </div>
          <div className="space-y-2">
            <Label>Date d'échéance</Label>
            <DatePicker
              value={formData.due_date}
              onChange={(iso) => setFormData(prev => ({ ...prev, due_date: iso }))}
              testId="manual-due-date"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Période</Label>
            <Input placeholder="Ex: Mars 2026" value={formData.period} onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>TVA</Label>
            <Select value={formData.vat_rate.toString()} onValueChange={(v) => setFormData(prev => ({ ...prev, vat_rate: parseFloat(v) }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="8.1">8.1% (Standard)</SelectItem>
                <SelectItem value="0">0% (Reverse charge)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={2} />
        </div>
      </div>

      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex justify-between text-sm mb-2">
          <span>Total HT</span>
          <span className="font-medium tabular-nums">{formatCurrency(totalHT)}</span>
        </div>
        <div className="flex justify-between text-sm mb-2">
          <span>TVA ({formData.vat_rate}%)</span>
          <span className="font-medium tabular-nums">{formatCurrency(vatAmount)}</span>
        </div>
        <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
          <span>Total TTC</span>
          <span className="tabular-nums">{formatCurrency(totalTTC)}</span>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" className="rounded-lg" data-testid="manual-submit">
          Créer la facture
        </Button>
      </div>
    </form>
  );
};

// ============== ARCHIVES COMPONENT ==============
const ArchivesFactures = ({ clients, onRefresh }) => {
  const [archivedInvoices, setArchivedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState('Tous');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchArchived();
  }, []);

  const fetchArchived = async () => {
    try {
      const response = await axios.get(`${API_URL}/invoices/archived`);
      setArchivedInvoices(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (invoiceId) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoiceId}/unarchive`);
      toast.success('Facture désarchivée');
      fetchArchived();
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors du désarchivage');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredInvoices = archivedInvoices.filter(inv => {
    if (clientFilter !== 'Tous' && inv.client_id !== clientFilter) return false;
    
    if (dateFrom && inv.archived_at) {
      const archivedDate = new Date(inv.archived_at);
      const fromDate = new Date(dateFrom);
      if (archivedDate < fromDate) return false;
    }
    if (dateTo && inv.archived_at) {
      const archivedDate = new Date(inv.archived_at);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      if (archivedDate > toDate) return false;
    }
    
    return true;
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-48" data-testid="archive-client-filter">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Tous">Tous les clients</SelectItem>
            {clients.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Archivé du</label>
            <DatePicker
              value={dateFrom}
              onChange={(iso) => setDateFrom(iso)}
              className="w-44"
              testId="invoices-archives-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Au</label>
            <DatePicker
              value={dateTo}
              onChange={(iso) => setDateTo(iso)}
              className="w-44"
              testId="invoices-archives-date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="mt-4"
            >
              <X size={14} className="mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Archived invoices list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucune facture archivée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="archived-invoices-table">
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Montant TTC</th>
                  <th>Statut</th>
                  <th>Archivé le</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id} data-testid={`archived-invoice-${invoice.id}`}>
                    <td className="font-mono">{invoice.invoice_number}</td>
                    <td>{getClientName(invoice.client_id)}</td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td className="tabular-nums">{formatCurrency(invoice.total_ttc)}</td>
                    <td>
                      <Badge className={statusColors[invoice.status]}>{invoice.status}</Badge>
                    </td>
                    <td>{invoice.archived_at ? formatDate(invoice.archived_at) : '-'}</td>
                    <td>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDownloadPDF(invoice)} 
                          className="rounded-lg"
                          data-testid={`download-archived-${invoice.id}`}
                        >
                          <Download size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnarchive(invoice.id)}
                          className="text-primary rounded-lg"
                          data-testid={`unarchive-invoice-${invoice.id}`}
                        >
                          <ArchiveRestore size={16} className="mr-1" />
                          Désarchiver
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== HISTORY & STATS COMPONENT ==============
const HistoriqueStats = ({ clients, onRefresh }) => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState('Tous');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [invoicesRes, statsRes] = await Promise.allSettled([
      axios.get(`${API_URL}/invoices`),
      axios.get(`${API_URL}/invoices/stats/summary`)
    ]);

    if (invoicesRes.status === 'fulfilled') {
      setInvoices(invoicesRes.value.data || []);
    } else {
      console.error('Invoices load failed:', invoicesRes.reason);
      toast.error("Erreur lors du chargement des factures");
      setInvoices([]);
    }

    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data || null);
    } else {
      console.error('Stats load failed:', statsRes.reason);
      toast.error("Erreur lors du chargement des statistiques");
      setStats(null);
    }

    setLoading(false);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (clientFilter !== 'Tous' && inv.client_id !== clientFilter) return false;
    if (statusFilter !== 'Tous' && inv.status !== statusFilter) return false;
    
    // Filtre par période
    if (dateFrom) {
      const invoiceDate = new Date(inv.invoice_date);
      const fromDate = new Date(dateFrom);
      if (invoiceDate < fromDate) return false;
    }
    if (dateTo) {
      const invoiceDate = new Date(inv.invoice_date);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59); // Inclure toute la journée
      if (invoiceDate > toDate) return false;
    }
    
    return true;
  });

  const resetPeriodFilter = () => {
    setDateFrom('');
    setDateTo('');
  };

  const handleUpdateStatus = async (invoiceId, status) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoiceId}/status?status=${status}`);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendEmail = async (invoice) => {
    try {
      const response = await axios.post(`${API_URL}/invoices/${invoice.id}/send`);
      toast.success(response.data.message || 'Email envoyé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // ---- WinBIZ CSV export (bizexdoc) ----
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const allFilteredIds = filteredInvoices.map(inv => inv.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.includes(id));
  const handleSelectAll = () => setSelectedIds(allFilteredIds);
  const handleDeselectAll = () => setSelectedIds([]);

  const handleExportWinbiz = async () => {
    if (selectedIds.length === 0) return;
    setExporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/invoices/export-winbiz`,
        { invoice_ids: selectedIds },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=windows-1252' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bizexdoc_factures_PT.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedIds.length} facture(s) exportée(s) vers WinBIZ`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'export';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    try {
      await axios.delete(`${API_URL}/invoices/${selectedInvoice.id}`);
      toast.success('Facture supprimée');
      setShowDeleteConfirm(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleArchive = async (invoice) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoice.id}/archive`);
      toast.success('Facture archivée');
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleArchiveAllPaid = async () => {
    try {
      const response = await axios.post(`${API_URL}/invoices/archive-paid`);
      toast.success(response.data.message);
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const canArchive = (invoice) => {
    return invoice?.status === 'Payée';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  const monthlyChartData = stats?.monthly_revenue?.map(m => ({
    name: getMonthName(m.month).substring(0, 3),
    value: m.value
  })) || [];

  const cumulativeData = monthlyChartData.reduce((acc, item, index) => {
    const cumValue = (acc[index - 1]?.cumulative || 0) + item.value;
    acc.push({ ...item, cumulative: cumValue });
    return acc;
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="widgets-grid">
        <div className="widget-card">
          <p className="text-sm text-slate-500 mb-1">CA année en cours</p>
          <p className="text-2xl font-bold tabular-nums">{formatCurrency(stats?.ca_year || 0)}</p>
        </div>
        <div className="widget-card">
          <p className="text-sm text-slate-500 mb-1">CA mois en cours</p>
          <p className="text-2xl font-bold tabular-nums">{formatCurrency(stats?.ca_month || 0)}</p>
        </div>
        <div className="widget-card">
          <p className="text-sm text-slate-500 mb-1">Factures ce mois</p>
          <p className="text-2xl font-bold tabular-nums">{stats?.invoices_month || 0}</p>
        </div>
        <div className="widget-card">
          <p className="text-sm text-slate-500 mb-1">Montant moyen</p>
          <p className="text-2xl font-bold tabular-nums">{formatCurrency(stats?.avg_invoice || 0)}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="widget-card">
          <h3 className="font-semibold mb-4">Chiffre d'affaires par mois</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="value" fill="#175CD3" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="widget-card">
          <h3 className="font-semibold mb-4">Répartition par client</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats?.client_revenue || []}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name.substring(0, 10)}... (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {(stats?.client_revenue || []).map((_, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="widget-card">
        <h3 className="font-semibold mb-4">Évolution cumulative du CA</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={cumulativeData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => formatCurrency(v)} />
            <Line type="monotone" dataKey="cumulative" stroke="#175CD3" strokeWidth={2} dot={{ fill: '#175CD3' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-end">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Tous">Tous les clients</SelectItem>
            {clients.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Tous">Tous</SelectItem>
            <SelectItem value="Brouillon">Brouillon</SelectItem>
            <SelectItem value="Envoyée">Envoyée</SelectItem>
            <SelectItem value="Payée">Payée</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Filtre par période */}
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Du</label>
            <DatePicker
              value={dateFrom}
              onChange={(iso) => setDateFrom(iso)}
              className="w-44"
              testId="filter-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Au</label>
            <DatePicker
              value={dateTo}
              onChange={(iso) => setDateTo(iso)}
              className="w-44"
              testId="filter-date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetPeriodFilter}
              className="mt-4"
              data-testid="reset-period-filter"
            >
              <X size={14} className="mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Archive all paid button + WinBIZ export bar */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {filteredInvoices.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={allSelected ? handleDeselectAll : handleSelectAll}
                data-testid="toggle-select-all-invoices"
              >
                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </Button>
              <span className="text-sm text-slate-500 tabular-nums" data-testid="selected-count">
                {selectedIds.length} sélectionnée(s)
              </span>
            </>
          )}
          {selectedIds.length > 0 && (
            <Button
              onClick={handleExportWinbiz}
              disabled={exporting}
              data-testid="export-winbiz-btn"
            >
              <Download size={16} className="mr-2" />
              {exporting ? 'Export en cours...' : `Exporter WinBIZ (${selectedIds.length})`}
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          onClick={handleArchiveAllPaid}
          className="rounded-lg"
          data-testid="archive-all-paid-btn"
        >
          <Archive size={16} className="mr-2" />
          Archiver les factures payées
        </Button>
      </div>

      {/* Invoices list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="empty-state">
            <Receipt size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucune facture</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() => (allSelected ? handleDeselectAll() : handleSelectAll())}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th>Numéro</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Montant TTC</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td>
                      <Checkbox
                        checked={selectedIds.includes(invoice.id)}
                        onCheckedChange={() => toggleSelect(invoice.id)}
                        data-testid={`select-invoice-${invoice.id}`}
                      />
                    </td>
                    <td className="font-mono">{invoice.invoice_number}</td>
                    <td>{getClientName(invoice.client_id)}</td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td className="tabular-nums">{formatCurrency(invoice.total_ttc)}</td>
                    <td>
                      <Select value={invoice.status} onValueChange={(v) => handleUpdateStatus(invoice.id, v)}>
                        <SelectTrigger className="w-32 h-8">
                          <Badge className={statusColors[invoice.status]}>{invoice.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Brouillon">Brouillon</SelectItem>
                          <SelectItem value="Envoyée">Envoyée</SelectItem>
                          <SelectItem value="Payée">Payée</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoice(invoice); setShowPreview(true); }} className="rounded-lg">
                          <Eye size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(invoice)} className="rounded-lg">
                          <Download size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSendEmail(invoice)} className="rounded-lg">
                          <Send size={16} />
                        </Button>
                        {canArchive(invoice) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleArchive(invoice)} 
                            className="rounded-lg text-slate-500"
                            data-testid={`archive-invoice-${invoice.id}`}
                          >
                            <Archive size={16} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoice(invoice); setShowDeleteConfirm(true); }} className="rounded-lg text-red-500">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu de la facture</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="invoice-preview">
              <h2 className="text-xl font-bold mb-4">Performance Transmission</h2>
              <div className="flex justify-between mb-4">
                <div>
                  <p className="font-bold">FACTURE N° {selectedInvoice.invoice_number}</p>
                  <p>Date: {formatDate(selectedInvoice.invoice_date)}</p>
                  <p>Échéance: {formatDate(selectedInvoice.due_date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">Client:</p>
                  <p>{getClientName(selectedInvoice.client_id)}</p>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qté</th>
                    <th>Prix unit.</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.lines?.map((line, idx) => (
                    <tr key={idx}>
                      <td>{line.description}</td>
                      <td className="text-right">{line.quantity}</td>
                      <td className="text-right">{formatCurrency(line.unit_price)}</td>
                      <td className="text-right">{formatCurrency(line.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-right">
                <p>Total HT: {formatCurrency(selectedInvoice.total_ht)}</p>
                <p>TVA ({selectedInvoice.vat_rate}%): {formatCurrency(selectedInvoice.vat_amount)}</p>
                <p className="font-bold text-lg">Total TTC: {formatCurrency(selectedInvoice.total_ttc)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)} className="rounded-lg">Fermer</Button>
            <Button onClick={() => handleDownloadPDF(selectedInvoice)} className="rounded-lg">
              <Download size={16} className="mr-2" />
              Télécharger PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Supprimer la facture "{selectedInvoice?.invoice_number}" ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-lg bg-destructive">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== MAIN FACTURATION COMPONENT ==============
const Facturation = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('batch');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="facturation-page">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Facturation</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="batch" data-testid="tab-batch">
            <Users size={16} className="mr-2" />
            Facturation en lot
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">
            <FileText size={16} className="mr-2" />
            Facture manuelle
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <TrendingUp size={16} className="mr-2" />
            Historique & Statistiques
          </TabsTrigger>
          <TabsTrigger value="archives" data-testid="tab-archives">
            <Archive size={16} className="mr-2" />
            Archives
          </TabsTrigger>
        </TabsList>

        <TabsContent value="batch">
          <FacturationEnLot clients={clients} onComplete={fetchClients} />
        </TabsContent>

        <TabsContent value="manual">
          <FactureManuelle clients={clients} onComplete={fetchClients} />
        </TabsContent>

        <TabsContent value="history">
          <HistoriqueStats clients={clients} onRefresh={fetchClients} />
        </TabsContent>

        <TabsContent value="archives">
          <ArchivesFactures clients={clients} onRefresh={fetchClients} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Facturation;
